import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useClickOutside } from 'services/useClickOutside';

// Same visual language as tag badges on record cards/modal (see typeColors in
// tab_records). Kept here as the single source of truth for type -> color so
// card, modal and this dropdown never drift apart.
export const TYPE_COLORS = {
  Consultation: 'bg-blue-700',
  Exam: 'bg-blue-500',
  Procedure: 'bg-blue-400',
  Vaccine: 'bg-blue-200 text-blue-700',
  Informal: 'bg-blue-100 text-blue-700',
  Urgency: 'bg-red-400',
};

export const TYPE_OPTIONS = Object.keys(TYPE_COLORS);

const PANEL_MAX_H = 260;

const getPortalStyle = (buttonEl) => {
  if (!buttonEl) return {};
  const rect = buttonEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const openAbove = spaceBelow < PANEL_MAX_H && rect.top > spaceBelow;
  const left = Math.min(rect.left, window.innerWidth - 240 - 8);
  return {
    position: 'fixed',
    top: openAbove ? `${rect.top - PANEL_MAX_H - 8}px` : `${rect.bottom + 4}px`,
    left: `${Math.max(8, left)}px`,
    width: `${Math.min(240, window.innerWidth - 16)}px`,
    maxHeight: `${PANEL_MAX_H}px`,
    zIndex: 70,
  };
};

/**
 * Type selector for Health records. Trigger and options render the same tag
 * pill used on record cards (typeColors), instead of plain select text —
 * closes the visual loop between filter/form and the read view.
 * Mirrors travel/moduleComponents/DropdownCountrySelect (light, portal-based).
 */
const DropdownType = ({
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Select type',
  showAllOption = false,
  allOptionText = 'All types',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useClickOutside([buttonRef, panelRef], () => setIsOpen(false), isOpen);

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setIsOpen((o) => !o);
        }}
        className={`flex items-center justify-between gap-2 w-full rounded-lg px-2 py-1.5 text-left bg-gray-100 hover:bg-gray-200 border-0 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-400 ${className}`}
      >
        {value ? (
          <span className={`px-2 py-0.5 text-xs rounded-full text-white ${TYPE_COLORS[value] || 'bg-gray-400'}`}>
            {value}
          </span>
        ) : (
          <span className="text-xs text-gray-400">{showAllOption ? allOptionText : placeholder}</span>
        )}
        <ChevronDownIcon className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            style={getPortalStyle(buttonRef.current)}
            className="bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col overflow-y-auto p-1"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {showAllOption && (
              <button
                type="button"
                onClick={() => handleSelect('')}
                className={`w-full flex items-center px-2 py-1.5 rounded text-left text-xs hover:bg-gray-100 ${value === '' ? 'bg-gray-100' : ''}`}
              >
                <span className="text-gray-500">{allOptionText}</span>
              </button>
            )}
            {TYPE_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleSelect(option)}
                className={`w-full flex items-center px-2 py-1.5 rounded text-left hover:bg-gray-100 ${value === option ? 'bg-gray-100' : ''}`}
              >
                <span className={`px-2 py-0.5 text-xs rounded-full text-white ${TYPE_COLORS[option]}`}>
                  {option}
                </span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default DropdownType;
